import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild
} from '@angular/core';

import {
  SkyFileItem
} from './file-item';

import {
  SkyFileLink
} from './file-link';

import {
  SkyFileDropChange
} from './file-drop-change';


export class SkyFileDropComponent {
  
  public filesChanged = new EventEmitter<SkyFileDropChange>();

  
  public linkChanged = new EventEmitter<SkyFileLink>();

  
  public minFileSize: number = 0;

  
  public maxFileSize: number = 500000;

  
  public multiple: boolean = true;

  
  public validateFn: Function;

  
  public acceptedTypes: string;

  
  public noClick: boolean = false;

  
  public allowLinks: boolean = false;

  
  public inputEl: ElementRef;

  public rejectedOver: boolean = false;
  public acceptedOver: boolean = false;
  public linkUrl: string;

  private enterEventTarget: any;

  public dropClicked() {
    if (!this.noClick) {
      this.inputEl.nativeElement.click();
    }
  }

  public fileChangeEvent(fileChangeEvent: any) {
    this.handleFiles(fileChangeEvent.target.files);
  }

  public fileDragEnter(dragEnterEvent: any) {
    // Save this target to know when the drag event leaves
    this.enterEventTarget = dragEnterEvent.target;
    dragEnterEvent.stopPropagation();
    dragEnterEvent.preventDefault();
  }

  public fileDragOver(dragOverEvent: any) {
    const transfer = dragOverEvent.dataTransfer;

    dragOverEvent.stopPropagation();
    dragOverEvent.preventDefault();

    if (transfer) {
      if (transfer.items) {
        const files = transfer.items;

        for (let index = 0; index < files.length; index++) {
          const file: any = files[index];

          if (file.type && this.fileTypeRejected(file.type)) {
            this.rejectedOver = true;
            this.acceptedOver = false;
            return;
          }
        }

        if (files.length > 0 && !this.acceptedOver) {
          this.rejectedOver = false;
          this.acceptedOver = true;
        }

      } else if (transfer.files) {
        // If the browser does not support DataTransfer.items,
        // defer file-type checking to drop handler.
        // https://developer.mozilla.org/en-US/docs/Web/API/DataTransfer/items#Browser_compatibility
        this.rejectedOver = false;
        this.acceptedOver = true;
      }
    }
  }

  public fileDrop(dropEvent: any) {
    dropEvent.stopPropagation();
    dropEvent.preventDefault();

    this.enterEventTarget = undefined;
    this.rejectedOver = false;
    this.acceptedOver = false;

    if (dropEvent.dataTransfer && dropEvent.dataTransfer.files) {
      if (this.verifyDropFiles(dropEvent.dataTransfer.files)) {
        this.handleFiles(dropEvent.dataTransfer.files);
      }
    }
  }

  public fileDragLeave(dragLeaveEvent: any) {
    if (this.enterEventTarget === dragLeaveEvent.target) {
      this.rejectedOver = false;
      this.acceptedOver = false;
    }
  }

  public addLinkEnter(event: KeyboardEvent) {
    if (event.which === 13) {
      this.addLink(event);
    }
  }

  public addLink(event: Event) {
    event.preventDefault();
    this.linkChanged.emit({ url: this.linkUrl } as SkyFileLink);
    this.linkUrl = undefined;
  }

  private emitFileChangeEvent(
    totalFiles: number,
    rejectedFileArray: Array<SkyFileItem>,
    validFileArray: Array<SkyFileItem>
  ) {
    if (totalFiles === rejectedFileArray.length + validFileArray.length) {
      this.filesChanged.emit({
        files: validFileArray,
        rejectedFiles: rejectedFileArray
      } as SkyFileDropChange);

      this.inputEl.nativeElement.value = '';
    }
  }

  private filesRejected(
    file: SkyFileItem,
    validFileArray: Array<SkyFileItem>,
    rejectedFileArray: Array<SkyFileItem>,
    totalFiles: number
  ) {
    rejectedFileArray.push(file);
    this.emitFileChangeEvent(totalFiles, rejectedFileArray, validFileArray);
  }

  private loadFile(
    fileDrop: SkyFileDropComponent,
    file: SkyFileItem,
    validFileArray: Array<SkyFileItem>,
    rejectedFileArray: Array<SkyFileItem>,
    totalFiles: number
  ) {
    const reader = new FileReader();

    reader.addEventListener('load', (event: any) => {
      file.url = event.target.result;
      validFileArray.push(file);
      fileDrop.emitFileChangeEvent(totalFiles, rejectedFileArray, validFileArray);
    });

    reader.addEventListener('error', (event: any) => {
      fileDrop.filesRejected(file, validFileArray, rejectedFileArray, totalFiles);
    });

    reader.addEventListener('abort', (event: any) => {
      fileDrop.filesRejected(file, validFileArray, rejectedFileArray, totalFiles);
    });

    reader.readAsDataURL(file.file);
  }

  private getMimeSubtype(type: string) {
    return type.substr(type.indexOf('/') + 1, type.length);
  }

  private getMimeMainType(type: string) {
    return type.substr(0, type.indexOf('/'));
  }

  private fileTypeInArray(typeArray: string[], fileType: string) {
    if (typeArray.indexOf(fileType) !== -1) {
      return true;
    }

    for (let index = 0; index < typeArray.length; index++) {
      const type = typeArray[index];
      const validSubtype = this.getMimeSubtype(type);

      if (validSubtype === '*') {
        if (this.getMimeMainType(type) === this.getMimeMainType(fileType)) {
          return true;
        }
      }
    }

    return false;
  }

  private fileTypeRejected(fileType: string) {
    if (!this.acceptedTypes) {
      return false;
    }

    if (!fileType) {
      return true;
    }

    let acceptedTypesUpper = this.acceptedTypes.toUpperCase();
    let typeArray = acceptedTypesUpper.split(',');

    return !this.fileTypeInArray(typeArray, fileType.toUpperCase());
  }

  private handleFiles(files: FileList) {
    let validFileArray: Array<SkyFileItem> = [];
    let rejectedFileArray: Array<SkyFileItem> = [];
    let totalFiles = files.length;
    let fileDrop = this;

    for (let index = 0; index < files.length; index++) {
      let fileItem = {
        file: files.item(index)
      } as SkyFileItem;

      if (fileItem.file.size < this.minFileSize) {
        fileItem.errorType = 'minFileSize';
        fileItem.errorParam = this.minFileSize.toString();
        this.filesRejected(fileItem, validFileArray, rejectedFileArray, totalFiles);

      } else if (fileItem.file.size > this.maxFileSize) {
        fileItem.errorType = 'maxFileSize';
        fileItem.errorParam = this.maxFileSize.toString();
        this.filesRejected(fileItem, validFileArray, rejectedFileArray, totalFiles);

      } else if (this.fileTypeRejected(fileItem.file.type)) {
        fileItem.errorType = 'fileType';
        fileItem.errorParam = this.acceptedTypes;
        this.filesRejected(fileItem, validFileArray, rejectedFileArray, totalFiles);

      } else if (this.validateFn) {
        let errorParam = this.validateFn(fileItem);

        if (!!errorParam) {
          fileItem.errorType = 'validate';
          fileItem.errorParam = errorParam;
          this.filesRejected(fileItem, validFileArray, rejectedFileArray, totalFiles);

        } else {
          this.loadFile(fileDrop, fileItem, validFileArray, rejectedFileArray, totalFiles);
        }

      } else {
        this.loadFile(fileDrop, fileItem, validFileArray, rejectedFileArray, totalFiles);
      }
    }
  }

  private verifyDropFiles(files: any) {
    if (!this.multiple && files.length > 1) {
      return false;
    }

    for (let index = 0; index < files.length; index++) {
      const file = files.item(index);

      if (file.webkitGetAsEntry && file.webkitGetAsEntry() && file.webkitGetAsEntry().isDirectory) {
        return false;
      }
    }

    return true;
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'sky-file-drop',
  template: `<div
  class="sky-file-drop-row" [ngClass]="{ 'sky-file-drop-allow-links': allowLinks }">
  <div
    class="sky-file-drop-col"
    [ngClass]="{ 'sky-file-drop-accept': acceptedOver, 'sky-file-drop-reject': rejectedOver }">

    <button
      class="sky-file-drop sky-file-drop-target"
      type="button"
      (click)="dropClicked()"
      (dragover)="fileDragOver($event)"
      (dragenter)="fileDragEnter($event)"
      (dragleave)="fileDragLeave($event)"
      (drop)="fileDrop($event)"
      [attr.aria-label]="'file_upload_drag_or_click' | skyResources">
    </button>

    <input
      tabindex="-1"
      aria-hidden="true"
      type="file"
      class="sky-file-input-hidden"
      [attr.multiple]="multiple ? multiple: null"
      [attr.accept]="acceptedTypes ? acceptedTypes: null"
      (change)="fileChangeEvent($event)"
      #fileInput>

    <div class="sky-file-drop-contents" *ngIf="customEl.children.length === 0">
      <div class="sky-file-drop-contents-not-over">
        <div class="sky-file-drop-text-header">
          {{ 'file_upload_drag_file_here' | skyResources }}
        </div>
        <div class="sky-file-drop-text">
          {{ 'file_upload_or_click_to_browse' | skyResources }}
        </div>
        <i class="fa fa-cloud-upload sky-file-upload-icon"></i>
      </div>

      <!-- This will appear when file is dragged over and is valid -->
      <div class="sky-file-drop-contents-accept" >
        <div class="sky-file-drop-text-header">
          {{ 'file_upload_drop_files_here' | skyResources }}
        </div>
        <i class="fa fa-bullseye sky-file-upload-icon"></i>
      </div>

      <!-- This will appear when file is dragged over and is invalid -->
      <div class="sky-file-drop-contents-reject" >
        <div class="sky-file-drop-text-header">
          {{ 'file_upload_invalid_file' | skyResources }}
        </div>
        <i class="fa fa-times-circle sky-file-upload-icon"></i>
      </div>
    </div>

    <div class="sky-file-drop-contents-custom" #customEl>
      <ng-content></ng-content>
    </div>
  </div>
  <div class="sky-file-drop-col" *ngIf="allowLinks">
    <div class="sky-file-drop-contents">
      <div class="sky-file-drop-link">
        <div class="sky-file-drop-link-header">
          <div class="sky-file-drop-text-header">
            {{ 'file_upload_paste_link' | skyResources }}
          </div>
        </div>
        <div class="sky-form-group">
          <input
            type="text"
            class="sky-form-control"
            [attr.placeholder]="'file_upload_link_placeholder' | skyResources"
            [(ngModel)]="linkUrl"
            (keyup)="addLinkEnter($event)"
            [attr.aria-label]="'file_upload_link_input' | skyResources">
        </div>
        <button
          type="button"
          class="sky-btn sky-btn-primary"
          [disabled]="!linkUrl"
          (click)="addLink($event)">
          {{ 'file_upload_paste_link_done' | skyResources }}
        </button>
      </div>
    </div>
  </div>
</div>
`,
  styles: [`.sky-file-drop-col {
  margin-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
  position: relative;
}

@media (min-width: 768px) {
  .sky-file-drop-row {
    display: flex;
  }
  .sky-file-drop-col {
    flex-basis: 100%;
  }
  .sky-file-drop-allow-links.sky-file-drop-col {
    flex-basis: 50%;
  }
}

button.sky-file-drop {
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
}

button.sky-file-drop:hover {
  cursor: pointer;
}

.sky-file-drop-contents {
  border-top: 1px solid #cdcfd2;
  border-bottom: 1px solid #cdcfd2;
  border-left: 1px solid #cdcfd2;
  border-right: 1px solid #cdcfd2;
  background-color: #eeeeef;
  padding: 10px;
  color: #282b31;
  cursor: pointer;
  width: 100%;
  text-align: center;
  height: 100%;
}

.sky-file-drop-contents .sky-file-drop-link-header {
  margin-bottom: 5px;
}

.sky-file-drop-noclick .sky-file-drop-contents {
  cursor: default;
}

.sky-file-drop-accept .sky-file-drop-contents,
.sky-file-drop-reject .sky-file-drop-contents {
  border-style: dashed;
  border-width: 1px;
}

.sky-file-drop-accept .sky-file-drop-contents-not-over,
.sky-file-drop-reject .sky-file-drop-contents-not-over {
  display: none;
}

.sky-file-drop-accept .sky-file-drop-contents {
  border-color: #71bf43;
  color: #282b31;
}

.sky-file-drop-accept .sky-file-drop-contents-accept {
  display: block;
}

.sky-file-drop-reject .sky-file-drop-contents {
  border-color: #ef4044;
  color: #282b31;
}

.sky-file-drop-reject .sky-file-drop-contents-reject {
  display: block;
}

.sky-file-drop-contents-accept,
.sky-file-drop-contents-reject {
  display: none;
}

.sky-file-upload-icon {
  display: block;
  font-size: 40px;
  margin-top: 10px;
}

.sky-file-drop-link {
  cursor: default;
}

.sky-file-drop-text-header {
  font-family: "Blackbaud Sans Condensed", "Helvetica Neue Condensed", "Arial Narrow";
  color: #282b31;
  font-weight: 500;
  font-size: 22px;
  margin: 0;
}

.sky-file-drop-text {
  font-size: 15px;
  margin-top: 5px;
  margin-bottom: 20px;
}

.sky-file-drop-text,
.sky-file-drop-text-header {
  line-height: 1.1;
  display: block;
}

.sky-file-upload-icon {
  color: #686c73;
}

.sky-file-drop {
  background-color: transparent;
  border: none;
  display: block;
  -webkit-appearance: none;
  width: 100%;
  padding: 0;
}

.sky-file-input-hidden {
  display: none;
}
`]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'filesChanged': [{ type: Output },],
'linkChanged': [{ type: Output },],
'minFileSize': [{ type: Input },],
'maxFileSize': [{ type: Input },],
'multiple': [{ type: Input },],
'validateFn': [{ type: Input },],
'acceptedTypes': [{ type: Input },],
'noClick': [{ type: Input },],
'allowLinks': [{ type: Input },],
'inputEl': [{ type: ViewChild, args: ['fileInput', ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
